import type { SUBSCRIPTION_STATUS_ENUM } from "@/utils/enums/subscription.enum";
import type { IApiResponse } from "..";
import type { IShiftInfo } from "@/types/timeAttendance";

export interface IUserRole {
  id: number;
  name: string;
  scope: string;
  // Immutable system-role identity (null for custom roles) — use this for
  // "is this the X role?" checks, never the renamable `name`.
  systemKey?: string | null;
}

export interface IUserCompensation {
  id: number;
  effectiveFrom: string | null;
  reviewDate: string | null;
  benefits: unknown[];
  earnings: unknown[];
  deductions: unknown[];
}

export interface IUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  profileImage: string | null;
  phoneNumber?: string | null;
  timeZone: string | null;
  redirectTo: EnumRedirectTo | null;
  isEmailVerified: boolean;
  companyId: number | null;
  branchId: number | null;
  isMfaEnabled: boolean;
  roleName: string;
  role?: IUserRole;
  impersonate?: boolean;
  employeeCode?: string;
  company?: {
    id: number;
    name: string;
    logo: string | null;
    logoIcon: string | null;
    email: string;
    companySubscription: {
      status: SUBSCRIPTION_STATUS_ENUM;
      currentPeriodEnd: string;
    };
  };
  employeeId?: string;
  employmentDetails?: {
    id: number;
    reportingManagerId: number | null;
    employeeId?: string;
    designation: string | null;
    reportingManager?: {
      id: number;
      firstName: string;
      lastName: string;
      email: string;
      profileImage: string | null;
    };
  };
  isCompanySuperAdmin: boolean;
  hasSubscriptionPermission: boolean;
  shift?: IShiftInfo;
  compensation?: IUserCompensation;
  customStatus?: string | null;
}

export enum EnumRedirectTo {
  OTP = "otp",
  HOME = "home",
  DASHBOARD = "dashboard",
  CREATE_COMPANY = "create-company",
  SUBSCRIPTION = "subscriptions",
}

export interface IPersonalDetails {
  id: number;
  email: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  profileImage: string | null;
  companyId: number;
  designation: string;
  reportingManagerId: number | null;
  reportingManager?: {
    id: number;
    firstName: string;
    lastName: string;
  } | null;
  gender: "male" | "female" | string; // can extend if you want strict enum
  dob: Date | null;
  maritalStatus: "single" | "married" | string;
  religion?: string | null;
  nationality: {
    name: string;
    nationality: string;
    id: number;
    countryCode: string;
  };
  nationalityId: number | string;
  street: string;
  countryId: number;
  state: string;
  city: string;
  zipCode: string;
  countryName: string;
  phone: string;
  whatsappNumber: string;
  emergencyName: string;
  emergencyRelation: string;
  emergencyPhone: string;
}

export interface IPasswordUpdateRequest {
  currentPassword: string;
  newPassword: string;
  isLogOutFromAllOtherDevices?: boolean;
}
export type TPersonalDetailsResponse = IApiResponse<IPersonalDetails>;

export type TBankDetailsResponse = IApiResponse<IBankDetailsData>;

export interface IBankDetailsData {
  holderName: string;
  accountNumber: string;
  iban: string;
  bankName: string;
  routingNumber: string;
  swiftCode: string;
  currency: string;
  branchName: string;
  branchCode: string;
  bankAddress: string;
}

export interface ISingleCompanyData {
  backgroundColorHash: string;
  id: number;
  name: string;
  portalName: string;
  industry: string;
  userId: number;
  phoneNumber: string;
  updatedAt: string;
  createdAt: string;
  logo: null | string;
  logoIcon: null | string;
  legalName: null | string;
  landLine: null | string;
  poBox: null | string;
  website: null | string;
  email: null | string;
  accessToken: null | string;
  refreshToken: null | string;
  deviceId: null | string;
}

export interface ICreateCompanyPayload {
  name: string;
  email: string;
  portalName: string;
  industry: string;
  phoneNumber: string;
}

export interface IEmployeeData {
  employeeId: string;
  joiningDate: string;
  typeOfContract: "permanent" | "temporary" | string;
  contractDuration: string;
  typeOfEmployment: "full_time" | "part_time" | string;
  workingHours: string;
  workingDays: string;
  probationPeriod: number;
  probationEndDate: string;
  role: string;
  department: string;
  designation: string;
  jobLevel: string;
  reportingManager: string;
  reportingOffice: string | undefined;
  companyEmail: string;
  companyPhone: string;
}
export type TEmploymentDetailsResponse = IApiResponse<IEmployeeData>;
